import { useState } from 'react';

import Asset from './Asset';
import UniversalLink from './UniversalLink';
import ParsedMarkdown from './ParsedMarkdown';

import { AtLeast } from '../../types/common';
import { Component } from '../../types/schema';
import { withCssModulesClassNames } from '../../common/nextMigrationHelpers';
import { default as compareCollectionStyles } from '../../scss/components/CompareCollection.module.scss';

const withCssModulesClassNamesHandler = withCssModulesClassNames(compareCollectionStyles);

type CompareCollectionCardProps = AtLeast<Component, 'copy' | 'styles' | 'points' | 'images' | 'header' | 'subHeader' | 'richTextCopy'>;

const CompareCollectionCard = ({
  copy,
  styles,
  points,
  images,
  header,
  subHeader,
  richTextCopy,
}: CompareCollectionCardProps) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const richTextCopyMarkdown = richTextCopy?.markdown
    && richTextCopy?.markdown.replace(/&lt;/g, '<');

  return (
    <div
      className={withCssModulesClassNamesHandler('compare-collection')}
      style={
        styles?.reduce((s, c) => {
          s[c.property] = c.value;

          return s;
        }, {} as Record<string, string>)
      }
    >
      <div className={withCssModulesClassNamesHandler('compare-collection-container')}>
        <div className={withCssModulesClassNamesHandler('compare-collection-rich-text')}>
          {
            richTextCopyMarkdown && (
              <ParsedMarkdown
                options={{
                  forceBlock: true,
                  overrides: {
                    UniversalLink: {
                      // eslint-disable-next-line react/display-name
                      component: ({ children, ...universalLinkProps }) => (
                        <UniversalLink type="Page" {...universalLinkProps}>{children}</UniversalLink>
                      ),
                    },
                  },
                }}
              >
                {richTextCopyMarkdown}
              </ParsedMarkdown>
            )
          }
        </div>
        <div className={withCssModulesClassNamesHandler('compare-collection-mobile-text')}>
          {
            copy && (
              <p className={withCssModulesClassNamesHandler('compare-collection-mobile-text-copy')}>{copy}</p>
            )
          }
          <h4>{header}</h4>
          <p>{subHeader}</p>
        </div>
        <div className={withCssModulesClassNamesHandler('compare-collection-imgs-container')}>
          {
            images?.map((asset) => (
              <Asset
                key={asset.handle}
                asset={asset}
                imageProps={{ blurryPlaceholder: false }}
              />
            ))
          }
        </div>
        <button
          className={withCssModulesClassNamesHandler('compare-collection-accordion-btn')}
          aria-label={`${isAccordionOpen ? 'Close' : 'Open'} collection details`}
          onClick={() => setIsAccordionOpen(!isAccordionOpen)}
        >
          <span>
            <i className={`icon icon-counter-modifier-${isAccordionOpen ? 'minus' : 'plus'}`} />
          </span>
        </button>
      </div>
      <div
        className={
          withCssModulesClassNamesHandler(
            'compare-collection-accordion-container',
            isAccordionOpen ? 'open-accordion' : 'close-accordion',
          )
        }
      >
        {
          points?.length && (
            <div className={withCssModulesClassNamesHandler('compare-collection-accordion-container-content')}>
              <Asset
                asset={points[0].images[0]}
                imageProps={{ blurryPlaceholder: false }}
              />
              <UniversalLink type="Page" {...points[0].links[0]}>{points[0].links[0].text}</UniversalLink>
            </div>
          )
        }
      </div>
    </div>
  );
};

export default CompareCollectionCard;
